import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button, Card, CardBody } from '@/shared/components';
import { I } from '@/shared/icons';
import { toApiError, useApi } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { propertyKeys } from '../queries';
import { useCreateWizard } from './useCreateWizard';
import { WIZARD_STEPS } from './wizardModel';
import { buildCreateRequest } from './wizardRequest';
import { StepLocation } from './StepLocation';
import { StepApartment } from './StepApartment';
import { StepOperations } from './StepOperations';

export function CreateWizard({ preselectedObjectId }: { preselectedObjectId?: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { form, patch, step, errors, goNext, goBack, canSubmit, touchedObjectValues } =
    useCreateWizard(preselectedObjectId);

  const create = useMutation({
    mutationFn: () => api.properties.create(buildCreateRequest(form, touchedObjectValues())),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      toast.success(`„${created.name}" als Entwurf angelegt`);
      void navigate({ to: '/properties/$id', params: { id: created.id } });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const isLast = step === 2;

  return (
    <div className="max-w-3xl">
      <ol className="mb-6 flex gap-2" aria-label="Fortschritt">
        {WIZARD_STEPS.map((s) => (
          <li key={s.id} className="flex-1">
            <div className={cn('h-1 rounded-full', s.id <= step ? 'bg-sage' : 'bg-line')} />
            <span
              className={cn(
                'mt-1.5 block text-xs',
                s.id === step ? 'text-ink font-medium' : 'text-ink-faint',
              )}
              aria-current={s.id === step ? 'step' : undefined}
            >
              {s.id + 1}. {s.label}
            </span>
          </li>
        ))}
      </ol>

      <Card>
        <CardBody className="p-6">
          {step === 0 && <StepLocation form={form} patch={patch} errors={errors} />}
          {step === 1 && <StepApartment form={form} patch={patch} errors={errors} />}
          {step === 2 && <StepOperations form={form} patch={patch} errors={errors} />}
        </CardBody>
      </Card>

      <div className="mt-6 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          leftIcon={<I name="ArrowLeft" size={14} />}
          onClick={step === 0 ? () => void navigate({ to: '/properties' }) : goBack}
        >
          {step === 0 ? 'Abbrechen' : 'Zurück'}
        </Button>
        {isLast ? (
          <Button
            type="button"
            variant="brand"
            loading={create.isPending}
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            Apartment anlegen
          </Button>
        ) : (
          <Button
            type="button"
            variant="brand"
            rightIcon={<I name="Arrow" size={14} />}
            onClick={goNext}
          >
            Weiter
          </Button>
        )}
      </div>
    </div>
  );
}
